import tw from "twin.macro";

import { useState } from "react";

import { RadioButtonChecked, RadioButtonUnchecked } from "@mui/icons-material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import {
  Autocomplete,
  AutocompleteProps,
  Checkbox,
  CircularProgress,
  InputBase,
  LinearProgress,
  autocompleteClasses,
  styled,
} from "@mui/material";

import { debounce, sortBy } from "lodash";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const StyledAutocompletePopper = styled("div")(() => ({
  [`&.${autocompleteClasses.popperDisablePortal}`]: tw`relative`,
  [`& .${autocompleteClasses.paper}`]: tw`m-0 text-sm shadow-none`,
  [`& .${autocompleteClasses.listbox}`]: {
    ...tw`p-0 bg-white`,
    [`& .${autocompleteClasses.option}`]: {
      ...tw`items-center min-h-0 p-0 border-t border-neutral-200`,
      '&[aria-selected="true"]': tw`bg-transparent!`,
      '&:hover, [aria-selected="true"]:hover, &.Mui-focused': tw`bg-neutral-100`,
    },
  },
}));

function PopperComponent(props) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { disablePortal, anchorEl, open, ...other } = props;
  return <StyledAutocompletePopper {...other} style={{ width: "auto" }} />;
}

const Loading = () => <CircularProgress tw="-mt-2" size={20} />;

type Option = {
  id: string;
  name: string;
};

export type AutocompletePopperProps<
  TOption = Option,
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
> = Omit<
  AutocompleteProps<TOption, Multiple, DisableClearable, false>,
  "renderInput"
> & {
  inputPlaceholder?: string;
  isLoading?: boolean;
  isFetching?: boolean;
  onQueryChange?: (val: string) => void;
  inputEndAdornment?: React.ReactNode;
};

const AutocompletePopper = <
  T extends Option,
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
>({
  value,
  options,
  inputPlaceholder,
  isLoading,
  isFetching,
  onQueryChange,
  inputEndAdornment,
  ...props
}: AutocompletePopperProps<T, Multiple, DisableClearable>) => {
  const [startingValue] = useState(value);

  const [searchValue, setSearchValue] = useState("");

  const getOpObj = (option: any) =>
    option.id ? option : options.find((o) => o.id === option);

  // Move the selected options to the top of the list
  const sortedOptions = sortBy([...options], (o) =>
    Array.isArray(startingValue)
      ? !startingValue.some((v) => (v.id ?? v) === o.id)
      : (startingValue?.id ?? startingValue) !== o.id
  );

  return (
    <div tw="flex-col">
      <Autocomplete
        value={value}
        open
        inputValue={searchValue}
        onInputChange={(_, val, reason) => {
          if (reason !== "input") return;
          setSearchValue(val);
          if (onQueryChange) {
            debounce(onQueryChange, 200)(val);
          }
        }}
        filterOptions={(options) =>
          options.filter((option) =>
            option.name.toLowerCase().includes(searchValue.toLowerCase())
          )
        }
        renderTags={() => null}
        noOptionsText={isLoading ? <Loading /> : "No options"}
        options={sortedOptions}
        isOptionEqualToValue={(option, value) =>
          option.id === getOpObj(value)?.id
        }
        getOptionLabel={(opt) => opt.name ?? opt}
        renderOption={({ key: _key, ...optProps }, option, { selected }) => (
          <li {...optProps} key={option.id} tw="py-0! px-0!">
            <Checkbox
              key={"checkbox"}
              icon={icon}
              checkedIcon={checkedIcon}
              checked={selected}
              {...(!props.multiple && {
                icon: <RadioButtonUnchecked fontSize="small" />,
                checkedIcon: <RadioButtonChecked fontSize="small" />,
              })}
            />
            <span tw="whitespace-nowrap pr-3" key={"name"}>
              {option.name}
            </span>
          </li>
        )}
        renderInput={(params) => {
          return (
            <>
              <InputBase
                tw="p-3 z-[1450]"
                autoFocus
                ref={params.InputProps.ref}
                inputProps={params.inputProps}
                placeholder={inputPlaceholder}
                endAdornment={inputEndAdornment}
              />
              <div tw="h-0.5 overflow-hidden">
                {isFetching && <LinearProgress />}
              </div>
            </>
          );
        }}
        slotProps={{
          ...props.slotProps,
          popper: { component: PopperComponent, ...props.slotProps?.popper },
        }}
        {...props}
      />
    </div>
  );
};

export default AutocompletePopper;
